// Global configuration for the Cinematic AI Console

export const COLORS = {
  hudGreen: "#7ef9c9",
  hudCyan: "#5ee7ff",
  hudDeepCyan: "#1fb6ff",
  hudAmber: "#f8d47a",
  textPrimary: "#e4f1f7",
  textDim: "#7e8b96",
  void: "#000000",
  bgDark: "#020309",
  bgMid: "#111827",
} as const;

export const PHYSICS = {
  breathingSpeed: 0.35,
  breathingAmount: 0.06,
  parallaxDamping: 0.03,
  parallaxStrength: 0.4,
  rotationSpeed: 0.004,
  cameraOscillationSpeed: 0.3,
  cameraOscillationAmount: 3.0,
} as const;

export const SCENE = {
  fogColor: 0x020309,
  fogNear: 40,
  fogFar: 260,
  cameraFov: 60,
  cameraY: 12,
  cameraZ: 120,
} as const;

export const LIGHTS = {
  ambientColor: 0x1fb6ff,
  ambientIntensity: 0.25,
  keyColor: 0x5ee7ff,
  keyIntensity: 2.1,
  rimColor: 0x7ef9c9,
  rimIntensity: 1.6,
  accentColor: 0xf8d47a,
  accentIntensity: 0.9,
  pulsing: true,
  pulseSpeed: 1.4,
  pulseAmount: 0.3,
} as const;

export const BRAIN = {
  nodeCount: 1400,
  nodeSize: 0.26,
  nodeColor: 0x5ee7ff,
  latticeColor: 0x1fb6ff,
  latticeOpacity: 0.35,
  latticeRebuildFrames: 120,
  latticeMaxConnections: 4200,
  latticeDistance: 14,
  pathCount: 18,
  pathColor: 0x7ef9c9,
  haloColor: 0x0b2530,
  haloOpacity: 0.6,
} as const;

export const CONTENT = {
  identity: "ARCHITECT OF RELIABILITY",
  domain: "FINTECH / DISTRIBUTED SYSTEMS / ENTERPRISE ARCHITECTURE",
  uptime: "16Y+",
  deployments: "100+ / PRODUCTION",
  mode: "ENGINEER OF SCALE / CO-FOUNDER",

  hero: {
    label: "Architect of Reliability · Engineer of Scale · Co-Founder",
    firstName: "Grzegorz",
    lastName: "Gajos",
    tagline: "Architecting resilience in a volatile digital landscape.",
    subtitle: `Owner of real, brutal, hands-on experience in diverse IT fields since 2009. Bridging the chasm between legacy infrastructure, cloud-native microservices, and AI-driven innovation. I don't just write code; I engineer trust through unwavering loyalty, fairness, and technical excellence.`,
  },

  panels: [
    {
      header: "The Architect of Reliability",
      content: `I operate at the critical junction where high-velocity systems meet complex integration landscapes. As a <strong>custodian of digital trust</strong>, I engineer API gateways and distributed transaction systems that serve as the backbone of financial operations. In these high-stakes environments, reliability is not a feature—it is the fundamental requirement. I deliver systems that withstand the pressure of scale, ensuring 99.99% uptime and regulatory compliance.`,
    },
    {
      header: "The Strategic Founder",
      content: `As <strong>Co-Founder of Open Tangerine</strong>, I understand that code does not exist in a vacuum. My tenure has been defined by the dual responsibility of architectural vision and business execution. From managing roadmaps and payroll to architecting scalable microservices, I bridge the gap between engineering perfectionism and business pragmatism. I build teams and systems that are as resilient as they are innovative.`,
    },
    {
      header: "The Solution Consultant",
      content: `Through extensive consultancy engagements with industry leaders like <strong>7N</strong>, I have mastered the art of turning ambiguous requirements into production-ready reality. My approach is rooted in deep technical understanding and a commitment to long-term value. Whether migrating infrastructure to Kubernetes or designing data portals, I bring a depth of experience that ensures stability, scalability, and success in the most challenging environments.`,
    },
  ],

  stats: [
    { number: "16+", label: "Years of Brutal Experience" },
    { number: "0", label: "Downtime Tolerance" },
    { number: "99.999%", label: "Desired Uptime" },
    { number: "∞", label: "Scale Ambition" },
  ],

  technologies: {
    backend: [
      { name: "Java", level: 95 },
      { name: "Kotlin", level: 88 },
      { name: "Spring Boot", level: 90 },
      { name: "Groovy", level: 87 },
      { name: "Microservices", level: 92 },
      { name: "Service Mesh", level: 85 },
      { name: "Distributed Systems", level: 88 },
    ],
    "data & messaging": [
      { name: "MongoDB", level: 87 },
      { name: "PostgreSQL", level: 85 },
      { name: "MySQL", level: 85 },
      { name: "BigQuery", level: 80 },
      { name: "Redis", level: 80 },
      { name: "Elasticsearch", level: 82 },
      { name: "Kafka", level: 88 },
      { name: "Hermes", level: 85 },
    ],
    "cloud & devops": [
      { name: "AWS", level: 83 },
      { name: "Azure", level: 70 },
      { name: "Docker", level: 88 },
      { name: "Kubernetes", level: 75 },
      { name: "Terraform", level: 82 },
      { name: "CI/CD", level: 84 },
      { name: "Git", level: 92 },
      { name: "Maven", level: 85 },
      { name: "Prometheus/Grafana", level: 80 },
      { name: "GitHub Actions", level: 85 },
    ],
    security: [
      { name: "JWT", level: 90 },
      { name: "JWS", level: 88 },
      { name: "OAuth 2.0 / OIDC", level: 85 },
      { name: "mTLS", level: 82 },
      { name: "Spring Security", level: 88 },
      { name: "OWASP", level: 85 },
      { name: "Pen Testing", level: 75 },
    ],
    ai: [
      { name: "GitHub Copilot", level: 92 },
      { name: "Claude/Gemini", level: 90 },
      { name: "Suno AI", level: 85 },
      { name: "AI Visual Models", level: 83 },
      { name: "Agentic Workflows", level: 88 },
      { name: "LLM Integration", level: 86 },
    ],
    frontend: [
      { name: "JavaScript", level: 82 },
      { name: "TypeScript", level: 80 },
      { name: "React.js", level: 80 },
      { name: "Next.js", level: 78 },
      { name: "Tailwind CSS", level: 85 },
      { name: "HTML5/CSS3", level: 85 },
      { name: "Vite", level: 75 },
    ],
  },

  timeline: [
    {
      year: "2021—Present",
      role: "Software Engineer",
      company: "Allegro",
      description: `Architecting high-velocity backend ecosystems at the intersection of e-commerce, fintech, and AI. I engineer the API gateways and distributed transaction layers that underpin critical financial operations. In a domain where downtime equates to regulatory breach and financial hemorrhage, I deliver 99.99% availability through rigorous system design and zero-downtime migration strategies.`,
      projects: [
        {
          name: "Merchant Finance Platform",
          description: `Building secure integrations with strategic banking partners using Kotlin, MongoDB, and service mesh architecture. Engineering API gateways with JWT/JWS security, managing distributed transactions across microservices, coordinating zero-downtime migrations for business-critical financial products.`,
          tech: "Kotlin, MongoDB, Kafka, Hermes, Service Mesh, JWT, JWS, BigQuery, Microservices, AI Integration",
        },
      ],
    },
    {
      year: "2012—Present",
      role: "Co-Founder & Owner",
      company: "Open Tangerine",
      description: `As Co-Founder, I navigate the complex interplay between technical purity and market reality. My role encompasses the entire product lifecycle—from strategic roadmapping to the architectural trenches. I build not just software, but sustainable businesses, bridging the gap between engineering ambition and commercial viability through transparent leadership and execution.`,
      projects: [
        {
          name: "Meveto",
          role: "CTO",
          description: `Assembled team of dedicated high-end professionals. Architected highly scalable, microservice-oriented system with latest standards in continuous integration, deployment and monitoring. Managed complete technical and business spectrum.`,
          tech: "Spring Boot, Java, React, AWS, CI/CD, Microservices",
        },
        {
          name: "Viking",
          role: "Lead Developer",
          description: `Supported developers and architected massively parallel AWS Lambda-based solution. Introduced performance and memory optimizations. Designed solutions for very legacy, heterogeneous environments under high pressure.`,
          tech: "Java, Spring, AWS Lambda, AWS Fargate, CloudFormation, Docker",
        },
        {
          name: "Contender",
          role: "Software Architect, Lead Developer",
          description: `Prepared project estimations, development plans and team organization. Defined architecture and selected application technology stack. Provided Agile development process consultation.`,
          tech: "Java, JavaScript, MongoDB, Google Guice, Scala, Spring, Play Framework",
        },
        {
          name: "Teamed.io",
          role: "Architect",
          description: `Created desktop application elements rendering digital data via interactive charts. Engineered distributed computation daemons in Hadoop-like environment. Built elements of Dropbox-like application based on S3 storage. Performed regular code reviews.`,
          tech: "Java 8, JavaFX, Swing, Amazon S3",
        },
      ],
    },
    {
      year: "2017—Present",
      role: "Technical Leader & Architect",
      company: "7N",
      description: `Delivering high-impact architectural leadership for enterprise clients. As an embedded consultant, I integrate deeply with client teams to drive technical transformation from within. I turn ambiguity into architecture, translating complex business requirements into robust, scalable solutions. My focus is on long-term value—building systems that persist and teams that thrive.`,
      projects: [
        {
          name: "Roche - R&D Dataset Portal",
          role: "Technical Lead",
          description: `Prepared, planned and executed successful infrastructure migration from EC2 to EKS. Led main execution of Kubernetes & Terraform setup including implementation, configuration and troubleshooting. Engaged with management and stakeholders, defined coding standards, worked with Business Analyst on technical user stories, participated in architecture reviews and governance.`,
          tech: "Terraform, AWS, Python, Docker, EC2, EKS, Linux, Solr, PostgreSQL, Kubernetes, CI/CD",
        },
        {
          name: "Roche - Thesaurus Management System",
          role: "Solution Architect",
          description: `Worked with business groups to understand processes and information needs. Identified and designed solution options supporting business needs. Developed and maintained roadmap aligned with business strategies and technology trends. Drove implementation and technical leadership to achieve vision.`,
          tech: "Oracle Forms, Oracle 19c, ETL, Kubernetes, Docker, Linux",
        },
        {
          name: "Roche - SIP",
          role: "Technical Lead",
          description: `Converting architectural and business vision into working software in validated project. Introduced cloud-like technologies, CI/CD automations, microservice-oriented container-based infrastructure. Supported SCRUM introduction, coordinated development lifecycle, performed database performance optimizations and guided migrations.`,
          tech: "Java, Docker, Kubernetes, Rancher, Spring Boot, Oracle 19c, CI/CD, API Design",
        },
        {
          name: "Roche - GPRS",
          role: "Lead Developer",
          description: `Prepared solution documents with implementation approach. Established SCRUM process, coordinated development ensuring highest quality. Cooperated with business analysts, solution architects, and validation teams. Participated in architectural and HR planning decisions.`,
          tech: "Java, JBoss, JPA, Hibernate, Docker, Oracle 11g, JavaScript",
        },
      ],
    },
    {
      year: "2016—2017",
      role: "Senior Software Engineer",
      company: "Toptal Network",
      description: `Operating within the top 3% of global engineering talent. I delivered specialized solutions for high-load environments, optimizing massive datasets through CQRS patterns and modernizing legacy stacks. My tenure was marked by early adoption of container orchestration, demonstrating a forward-thinking approach to infrastructure and scalability.`,
      projects: [
        {
          name: "Appiness.mobi",
          role: "Backend Software Engineer",
          description: `Optimization of application reports. Created independent REST module for gathering and denormalization of raw data to provide fast queries on large datasets. SQL Server database tuning, data migration, module deployment and monitoring.`,
          tech: "Java, Spring, Docker Swarm, Swagger, SQL Server (Azure SQL), Git, Maven",
        },
        {
          name: "Glider Technology",
          role: "Full Stack Developer",
          description: `Created UI for building information modeling application. Converted static pages into highly responsive React.js website. Refactored codebase introducing code quality tools (static analysis, strong typing). Prepared modular front-end build process. Performed code reviews, took full responsibility for JavaScript codebase.`,
          tech: "JavaScript ES7, React.js, Gulp, MySQL, Redis, Elasticsearch, Docker, Git, TypeScript",
        },
      ],
    },
    {
      year: "2013—2016",
      role: "Enterprise Architect",
      company: "UWS Software Service",
      description: `Engineered enterprise-grade systems for major European markets. I pioneered zero-downtime deployment strategies using feature flags and successfully integrated modern Java architectures with complex legacy ecosystems. My work proved that true reliability stems from a deep understanding of both historical constraints and modern capabilities.`,
      projects: [
        {
          name: "Top Right",
          role: "Full Stack Developer",
          description: `Created microservice-oriented architecture to overcome monolith issues. Delivered estimations, designs, modularization and implementation of various projects to automate and stabilize the product. Managed production servers and deployment flow. Weeks of performance optimizations across database, back-end, and front-end layers.`,
          tech: "Java EE, JavaScript, PostgreSQL, Groovy, Grails, JBoss, JSP, Microservices, Git",
        },
        {
          name: "Messe München International",
          role: "Software Engineer",
          description: `Implementation of multiple web applications in fair organization workflow. Supported estimations and project planning. Delivered well-tested backend and external system integration modules.`,
          tech: "Java, JavaScript, MySQL, Oracle, Grails, Spring, Spock, Docker, Maven",
        },
        {
          name: "Bonial Group",
          role: "Lead, Software Engineer",
          description: `Implementation of new controllers/services maintaining backward compatibility using feature switch approach. Implemented frontend for responsive pages. Supported React.js brochure viewer creation. Requirements clarification with stakeholders.`,
          tech: "Java, Groovy, Grails, AWS, Linux, Chef, PostgreSQL, Gulp, React.js, Jira, Bamboo, SCRUM, Spock, JUnit",
        },
      ],
    },
    {
      year: "2010—2013",
      role: "Full-Stack Engineer",
      company: "itSilesia",
      description: `Architected comprehensive solutions from the ground up, ranging from veterinary practice management to geospatial search engines. This foundational period established my versatility across domains, mastering the full stack from database design to user interface, and cementing a deep understanding of the software lifecycle.`,
      projects: [
        {
          name: "Vetinf",
          role: "Java Developer",
          description: `Implemented entire application stack for veterinary practice management in client-server architecture. Full coverage: payments, invoicing, printing, external device integration, administration, data aggregation, security. Built unified DTO framework and distributed transaction management. Annotation-based constraint violation discovery algorithm. Generic Eclipse RCP sortable lazy table implementation.`,
          tech: "Java, Spring, Spring Security, Maven, AOP, Servlets, MySQL, Eclipse RCP",
        },
        {
          name: "nah-de",
          role: "Full-Stack Developer",
          description: `Yellow pages for German market. Implemented critical fast and intuitive geospatial search feature connected with feature-rich interactive map component.`,
          tech: "Groovy, Grails, Java, Solr, Geolocation, HTML5, CSS3, JavaScript, Google Maps, Jenkins, JUnit, TDD, MySQL, SCRUM",
        },
        {
          name: "atDent",
          role: "Full-Stack Developer",
          description: `Created full dental facility management application from scratch.`,
          tech: "Groovy, Grails, JavaScript, Dojo Toolkit, Raphael, MySQL",
        },
        {
          name: "Ejero",
          role: "Software Architect, Lead Developer",
          description: `Led social recommendation platform development that digitizes and tracks word of mouth. Responsible for everything from mobile application development to architecture, foundations, leading, recruiting, and platform operations.`,
          tech: "Grails, Java, HTML5, JavaScript, Jenkins, Unit Testing, TDD, Mobile Applications, MySQL",
        },
      ],
    },
    {
      year: "2009—2010",
      role: "Software Developer",
      company: "Apronet",
      description: `The foundational years. leveraging C# and .NET to build critical desktop applications for the medical sector. This experience laid the groundwork for a polyglot career, fostering a deep fluency across both .NET and JVM ecosystems that continues to inform my architectural decisions today.`,
      projects: [
        {
          name: "CRM Extension for Comarch Optima System",
          role: ".NET Developer",
          description: `Desktop application for flexible planning and calculating doctors' paycheck and time management for medical facilities. Integrated as external plugin for CRM (Comarch Optima system).`,
          tech: "C#, .NET Framework, Visual Studio, SQL Server",
        },
      ],
    },
  ],

  contact: {
    title: "Initialize Connection",
    links: [
      { label: "SEND_MESSAGE", href: "mailto:gagreg@gmail.com" },
      {
        label: "LINKEDIN_SYNC",
        href: "https://linkedin.com/in/ggajos",
        target: "_blank",
      },
      {
        label: "OPEN_TANGERINE",
        href: "https://opentangerine.com",
        target: "_blank",
      },
    ],
  },
} as const;
